/**
 * Zod-схемы валидации.
 *
 * Одни и те же схемы используются формами веб-клиента и обработчиками API:
 * правило «дата сдачи обязательна» не может разойтись между экраном и сервером,
 * потому что оно записано один раз.
 */
import { z } from 'zod';
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата должна быть в формате ГГГГ-ММ-ДД');
export const payMethodSchema = z.enum(['По договору', 'Наличные']);
export const orderStatusSchema = z.enum(['Новый', 'В очереди', 'В работе', 'Ожидание', 'Готово']);
export const urgencySchema = z.enum(['Авто', 'Очень срочно', 'Срочно', 'В работе', 'Готово']);
export const invoiceStatusSchema = z.enum(['Не отправлен', 'Отправлен', 'Оплачено']);
export const constructionTypeSchema = z.enum(['Базовая конструкция', 'Дополнительный элемент']);
export const roleSchema = z.enum(['owner', 'employee']);
export const employeePositionSchema = z.enum(['Техник', 'Администратор', 'Менеджер']);
export const themeNameSchema = z.enum(['teal', 'blue', 'gray', 'sage', 'rose', 'plum', 'coffee', 'ink', 'custom']);
/** Денежная сумма в копейках: целое, не отрицательное. */
export const kopSchema = z
    .number()
    .int('Сумма должна быть целым числом копеек')
    .min(0, 'Сумма не может быть отрицательной');
/** Денежная сумма, допускающая минус (доход месяца, остаток владельца). */
export const signedKopSchema = z.number().int();
export const orderExtraSchema = z.object({
    id: z.string().min(1),
    qty: z.number().int().min(1, 'Количество должно быть не меньше 1'),
});
/**
 * Заказ.
 *
 * Обязательны только ссылки на справочники — без них заказ бессмыслен: не
 * определится ни юрлицо, ни цена. Пациент и дата сдачи НЕ обязательны:
 * заказ часто заводят по звонку врача, когда фамилия и срок ещё не названы,
 * и мешать этому нельзя. Логика при этом не теряется — заказ без даты сдачи
 * считается «В работе», пока срок не проставят.
 */
export const orderInputSchema = z.object({
    /*
     * Врач НЕОБЯЗАТЕЛЕН — как клиника и конструкция.
     *
     * Работу привозят из клиники раньше, чем известно, кто из врачей её ведёт.
     * Пустая строка означает «пока не известен»; счёт по такому заказу
     * выставляется как обычно — он идёт клинике, а не врачу.
     */
    doctorId: z.string().default(''),
    /*
     * Клиника и филиал НЕОБЯЗАТЕЛЬНЫ: заказ бывает частным — пациент пришёл
     * сам, клиники за ним нет. Пустая строка означает «без клиники»; что
     * заполнены обе или ни одной, проверяет сервер при записи.
     */
    clinicId: z.string().default(''),
    branchId: z.string().default(''),
    /*
     * Конструкция НЕОБЯЗАТЕЛЬНА — как клиника. Заказ заводят по звонку врача,
     * когда изделие ещё не названо. Пустая строка означает «не выбрана», и
     * стоимость такого заказа нулевая.
     */
    constructionId: z.string().default(''),
    /*
     * Количество базовой конструкции. Целое и не меньше единицы: заказ на
     * «ноль аппаратов» бессмыслен, а половина аппарата не изготавливается.
     */
    constructionQty: z.number().int().min(1, 'Количество не меньше одного').default(1),
    /*
     * Челюсть. Пустая строка — по умолчанию и совершенно нормально: часть
     * аппаратов делается всегда на одну и ту же челюсть, и уточнять там нечего.
     * Тогда приписки в названии работы просто не появляется.
     */
    jaw: z.enum(['', 'Верхняя челюсть', 'Нижняя челюсть', 'Обе челюсти']).default(''),
    colors: z.array(z.string()).default([]),
    extras: z.array(orderExtraSchema).default([]),
    patient: z.string().trim().default(''),
    received: isoDateSchema,
    /** Пустая строка приводится к `null`: браузер шлёт '' из очищенного поля даты. */
    due: z
        .union([isoDateSchema, z.literal(''), z.null()])
        .transform((value) => (value === '' ? null : value))
        .nullable()
        .default(null),
    status: orderStatusSchema,
    urgency: urgencySchema,
    /*
     * Способ оплаты НЕОБЯЗАТЕЛЕН — как врач, клиника и конструкция.
     *
     * Он не свободный выбор, а свойство клиники, и подставляется вместе с ней.
     * Пустая строка означает «клиника ещё не названа»; в счёт такой заказ не
     * попадёт — счёт собирается из заказов по договору.
     */
    pay: z.union([payMethodSchema, z.literal('')]).default(''),
    paid: z.boolean(),
    costKop: kopSchema,
    expenseKop: kopSchema,
    notes: z.string().default(''),
});
export const legalInputSchema = z.object({
    name: z.string().trim().min(1, 'Укажите название юрлица'),
    form: z.string().default(''),
    shortForm: z.string().default(''),
    pay: payMethodSchema,
    contractNo: z.string().default(''),
    contractDate: z.string().default(''),
    contract: z.string().default(''),
    email: z.union([z.literal(''), z.string().email('Некорректная почта')]).default(''),
    inn: z.string().default(''),
    ogrn: z.string().default(''),
    kpp: z.string().default(''),
    address: z.string().default(''),
    bank: z.string().default(''),
    bik: z.string().default(''),
    rs: z.string().default(''),
    ks: z.string().default(''),
    signer: z.string().default(''),
    note: z.string().default(''),
    signerPosition: z.string().default(''),
    signerName: z.string().default(''),
    signerGenitive: z.string().default(''),
    warnOnReady: z.boolean().default(false),
    mergeInvoiceItems: z.boolean().default(false),
    invoicePatient: z.boolean().default(true),
    invoiceDoctor: z.boolean().default(false),
});
export const branchInputSchema = z.object({
    name: z.string().trim().min(1, 'Укажите название филиала'),
    legalId: z.string().min(1, 'Выберите юрлицо филиала'),
});
export const clinicInputSchema = z.object({
    name: z.string().trim().min(1, 'Укажите название клиники'),
    countDelivery: z.boolean().default(false),
    deliveryKop: kopSchema.max(2_147_483_647).default(0),
});
export const doctorInputSchema = z.object({
    name: z.string().trim().min(1, 'Укажите ФИО врача'),
    clinics: z.array(z.string()).default([]),
    phone: z.string().default(''),
    email: z.union([z.literal(''), z.string().email('Некорректная почта')]).default(''),
    telegram: z.string().default(''),
    whatsapp: z.string().default(''),
    max: z.string().default(''),
    notify: z.boolean().default(true),
    note: z.string().default(''),
});
export const constructionInputSchema = z.object({
    type: constructionTypeSchema,
    categoryId: z.string().min(1, 'Выберите категорию'),
    name: z.string().trim().min(1, 'Укажите название'),
    priceCashKop: kopSchema,
    priceContractKop: kopSchema,
    costKop: kopSchema,
    hasColor: z.boolean().default(false),
    qtyAdjustable: z.boolean().default(false),
    materials: z
        .array(z.object({ materialId: z.string().min(1), qty: z.number().min(0) }))
        .default([]),
});
export const invoiceItemInputSchema = z.object({
    id: z.string().min(1),
    position: z.number().int().min(0),
    name: z.string().trim().min(1, 'Название позиции не может быть пустым'),
    qty: z.number().min(0, 'Количество не может быть отрицательным'),
    unit: z.string().default(''),
    priceKop: kopSchema,
});
/**
 * Создание счёта.
 *
 * Номер приходит с клиента и может быть переопределён вручную (§2.2) — сервер
 * проверяет его на занятость в пределах года, но не навязывает своё значение.
 */
export const createInvoiceSchema = z.object({
    orderIds: z.array(z.string().min(1)).min(1, 'Выберите хотя бы один заказ'),
    number: z.number().int().min(1, 'Номер счёта должен быть положительным'),
    /*
     * Приписка к номеру — только руками, для разбора ошибок бумажного учёта.
     * Короткая и без пробелов: она печатается в шапке документа вплотную
     * к номеру, и «368- часть 1» там читалось бы как две разные величины.
     */
    numberSuffix: z
        .string()
        .trim()
        .max(8)
        .regex(/^[0-9A-Za-zА-Яа-я]*$/, 'Приписка — только буквы и цифры')
        .optional()
        .nullable(),
    date: isoDateSchema,
    items: z.array(invoiceItemInputSchema).min(1, 'В счёте должна быть хотя бы одна позиция'),
});
export const employeeInputSchema = z.object({
    name: z.string().trim().min(1, 'Укажите ФИО сотрудника'),
    position: employeePositionSchema,
    role: roleSchema,
    login: z.string().trim().min(3, 'Логин не короче 3 символов'),
    percent: z.number().min(0, 'Процент не может быть отрицательным').max(100, 'Процент не больше 100'),
    bonusKop: kopSchema,
    salaryAccess: z.boolean().default(false),
    blocked: z.boolean().default(false),
});
export const performerSchema = z.object({
    name: z.string().default(''),
    shortName: z.string().default(''),
    ogrnip: z.string().default(''),
    inn: z.string().default(''),
    address: z.string().default(''),
    phone: z.string().default(''),
    bank: z.string().default(''),
    bik: z.string().default(''),
    rs: z.string().default(''),
    ks: z.string().default(''),
    signer: z.string().default(''),
});
export const smtpSchema = z.object({
    host: z.string().default(''),
    port: z.string().default(''),
    login: z.string().default(''),
    /** `null` — пароль не меняется; сервер сохраняет прежнее зашифрованное значение. */
    password: z.string().nullable().default(null),
});
export const templatesSchema = z.object({
    invoiceTemplate: z.string().default(''),
    actTemplate: z.string().default(''),
    letter1: z.string().default(''),
    letter2: z.string().default(''),
});
export const loginSchema = z.object({
    login: z.string().trim().min(1, 'Введите логин'),
    password: z.string().min(1, 'Введите пароль'),
});
// ---------------------------------------------------------------------------
// Batch-коммит модуля (§6 в многопользовательской среде)
// ---------------------------------------------------------------------------
/** Модули, у которых есть собственная кнопка «Сохранить изменения». */
export const moduleNameSchema = z.enum([
    'orders',
    'clinics',
    'doctors',
    'constructions',
    'employees',
    'settings',
    'finance',
    'templates',
]);
/**
 * Пакет изменений модуля.
 *
 * Клиент правит черновик локально и отправляет его целиком одним запросом при
 * нажатии «Сохранить изменения». `version` каждой изменяемой записи — основа
 * оптимистичной блокировки: сервер откажет, если запись успел изменить кто-то
 * другой, вместо тихой перезаписи чужой правки.
 */
export const moduleCommitSchema = z.object({
    creates: z
        .array(z.object({ tempId: z.string().min(1), kind: z.string().min(1), data: z.unknown() }))
        .default([]),
    updates: z
        .array(z.object({
        id: z.string().min(1),
        kind: z.string().min(1),
        version: z.number().int().min(0),
        patch: z.record(z.unknown()),
    }))
        .default([]),
    deletes: z.array(z.object({ id: z.string().min(1), kind: z.string().min(1) })).default([]),
});
//# sourceMappingURL=schemas.js.map